import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Warehouse, Truck as TruckIcon, Loader2, ArrowUpRight, ClipboardList, FileWarning, Gavel, Printer, Trash2, Camera, Package, RotateCcw, Link2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import PrintReceipt from "../components/PrintReceipt";
import PrintReleaseFormButton from "../components/PrintReleaseFormButton";
import { listYardStock, listConfiguredYards, departYard, releaseJobToNts, releaseYardJobDva, fetchDvaReleasePdf, restoreYardJobToYard, relinkYardOutbound, getJob, listTrucks, fetchStorageRates, setJobStorageRateCategory, logBelongingsRelease, openReleaseFormPrint, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import JobDetailSheet from "../components/JobDetailSheet";
import { EmptyState } from "../components/EmptyState";
import { salvageFull, salvageLabel, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { fetchReleaseFormHtml, renderReleaseFormInTab, releaseFormError } from "../lib/releaseForm";
import { hasCap, can } from "../lib/roles";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

/**
 * YardStockPage — dedicated stock list of every non-impound vehicle sitting
 * in one of our storage yards (Parkers Rd, Blackbyre). Search-friendly (rego,
 * customer, program, WO #, AA job ID). Each row has:
 *   - Days in yard + running storage total
 *   - "On-tow out" button → creates the outbound tow job pre-filled and
 *     linked; storage stops the moment that outbound reaches on_site.
 *
 * Impounds are intentionally NOT here — they have their own pricing engine
 * and lifecycle in the Impounds module.
 */
export default function YardStockPage() {
  const { user } = useAuth();
  const isAdmin = hasCap(user, "admin");
  const canSignOver = can(user, "sign_over_vehicles");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yardFilter, setYardFilter] = useState("all");
  const [q, setQ] = useState("");
  const [departFor, setDepartFor] = useState(null);      // row currently in "On-tow out" dialog
  const [signOverFor, setSignOverFor] = useState(null);  // row currently in the sign-over dialog
  const [dvaFor, setDvaFor] = useState(null);            // row currently in "Released DVA" dialog
  // Feb 2026 — before the DVA dialog opens we ask whether to restart the
  // storage clock under the purchaser's name (auction houses like Turners
  // /Manheim cover storage until pickup — from then on the purchaser is
  // responsible). Holds the row while the Yes/No confirm sits open, plus
  // the answer once given ({row, restart: bool}).
  const [dvaConfirm, setDvaConfirm] = useState(null);
  // Storage-row deletion (admin-only). Cleans up mistaken storage entries
  // WITHOUT deleting the underlying job — safe alternative to the Danger
  // Zone purge that took out an impound in the NYP75 incident.
  const [removeFor, setRemoveFor] = useState(null);
  // Feb 2026 — belongings pickup dialog. Owners often come to collect
  // personal items (child seat, phone, tools) before the vehicle itself
  // is disposed of. Logs the pickup + opens the existing "Released
  // Personal Items" PDF for signing. Vehicle stays in the yard.
  const [belongingsFor, setBelongingsFor] = useState(null);
  // ↺ Restore-to-yard confirm. Only offered on DVA-released rows so the
  // dispatcher can undo an accidental auction release before the row
  // auto-archives at day 14. Server enforces the same guards.
  const [restoreFor, setRestoreFor] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  // Retro reverse-linker (A63707545 fix). Dispatcher taps the chain icon
  // on a yarded row that SHOULD have already been released by an
  // existing on-tow-out job → server searches History for a match and
  // glues them, stopping the storage clock.
  const [relinkBusy, setRelinkBusy] = useState(null); // row.id currently being relinked
  // Detail sheet — clicking a row (or the little clipboard icon) opens the
  // full read-only job panel INLINE on this page instead of navigating away
  // to History (which was often filtered to "Today" and so hid older
  // yarded jobs). Fetches the full job doc lazily on open — the yard-stock
  // row is only a lightweight summary.
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [truckById, setTruckById] = useState({});
  // Rate categories for the per-row override dropdown.
  const [rateCats, setRateCats] = useState([]);
  useEffect(() => {
    fetchStorageRates()
      .then((r) => setRateCats(r.categories || []))
      .catch(() => { /* non-fatal — dropdown just shows Default/Auto */ });
  }, []);
  useEffect(() => {
    listTrucks().then((ts) => {
      const m = {};
      ts.forEach((t) => { m[t.id] = t; });
      setTruckById(m);
    }).catch(() => { /* non-fatal — sheet just shows raw truck ID */ });
  }, []);

  const openDetail = useCallback(async (rowOrId) => {
    const id = typeof rowOrId === "string" ? rowOrId : rowOrId?.id;
    if (!id) return;
    setDetailLoadingId(id);
    try {
      const job = await getJob(id);
      setDetailJob(job);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load job");
    } finally {
      setDetailLoadingId(null);
    }
  }, []);

  // Dynamic list of configured yards — pulled from the impound config so
  // the tab slugs match whatever the admin actually named them. Prevents
  // the "Parkers Rd" vs "Parkers Road" slug mismatch that would break
  // filtering when the seeded name differed from a hardcoded expectation.
  const [yardsConfig, setYardsConfig] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listConfiguredYards()
      .then((ys) => { if (!cancelled) setYardsConfig(ys); })
      .catch(() => { /* non-fatal — tabs just fall back to "All Yards" */ });
    return () => { cancelled = true; };
  }, []);

  // Request-ID token: increments on every fetch dispatch. When a response
  // comes back we compare its captured id to the current one and drop it
  // if a newer request has fired since. Kills the "old response arrives
  // late and clobbers the filtered result" race that the previous
  // double-useEffect pattern was hitting (Aug 2026).
  const reqIdRef = useRef(0);

  const refresh = useCallback(async (showSpinner = false) => {
    const myReqId = ++reqIdRef.current;
    // Silent by default. Typing in the search box or switching yards updates
    // the rows underneath rather than blanking the list on every keystroke;
    // only the first load shows the spinner.
    if (showSpinner) setLoading(true);
    try {
      const data = await listYardStock({ yard: yardFilter, q });
      // Drop stale response — a newer fetch has fired since this one started.
      if (myReqId !== reqIdRef.current) return;
      setRows(data);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(e.response?.data?.detail || "Failed to load yard stock");
    } finally {
      // Clear on the LATEST request regardless of who set it: a silent
      // refetch overtaking a spinner request would otherwise leave the
      // spinner up forever (seen on Storage Yards, Oct 2026).
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [yardFilter, q]);

  // Yard-filter changes fire immediately (tab switch). Search-query changes
  // are debounced 300ms so we're not hitting the endpoint on every keystroke.
  // Note: only ONE effect owns the fetch trigger now — the previous version
  // had TWO effects racing (a "fire on refresh change" one AND a debounced
  // one) which caused up to 4 in-flight requests per typed word and
  // occasional list-reset when a stale response won the race.
  // First paint shows the spinner; yard switches and typing refresh silently.
  useEffect(() => { refresh(rows.length === 0); }, [yardFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => refresh(false), 300);
    return () => clearTimeout(t);
  }, [q]);  // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => {
    const totalDays = rows.reduce((a, r) => a + (r.days_in_storage || 0), 0);
    const totalStorage = rows.reduce((a, r) => a + (r.storage_total || 0), 0);
    return { count: rows.length, totalDays, totalStorage };
  }, [rows]);

  // Aug 2026 — printable "Stock Take" sheet. Fetches EVERY yard's live
  // stock (ignoring the current UI yard filter + search), groups by yard,
  // and pops a new browser tab with a print-friendly A4 layout. Nothing
  // in the app state changes — the button is a pure side-window action.
  const [printingStockTake, setPrintingStockTake] = useState(false);
  const onPrintStockTake = useCallback(async () => {
    setPrintingStockTake(true);
    try {
      // in_yard_only=true — the stock-take sheet must only list vehicles
      // that should physically still be in the yard. DVA-released rows
      // (the 14-day auction hand-off window) show a red badge on the live
      // page but must not appear on the printed walk-the-yard list.
      const data = await listYardStock({ yard: "all", q: "", inYardOnly: true });
      // Group by yard slug → keep the app's `yards` config order so the
      // sheet's section order matches the filter-tab order the user sees.
      const bySlug = new Map();
      for (const r of data) {
        const key = r.storage_yard || "unassigned";
        if (!bySlug.has(key)) bySlug.set(key, []);
        bySlug.get(key).push(r);
      }
      const slugOrder = [
        ...yardsConfig.map((y) => y.slug),
        ...[...bySlug.keys()].filter((k) => !yardsConfig.find((y) => y.slug === k)),
      ];
      const yardNameFor = (slug) => yardsConfig.find((y) => y.slug === slug)?.name || slug;
      const now = new Date();
      const stampNz = new Intl.DateTimeFormat(tenantLocale(), {
        weekday: "long", day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
        timeZone: tenantTz(),
      }).format(now);
      const dateOnly = new Intl.DateTimeFormat(tenantLocale(), {
        day: "2-digit", month: "2-digit", year: "numeric",
        timeZone: tenantTz(),
      }).format(now);
      const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
      ));
      const fmtDropOff = (iso) => {
        if (!iso) return "—";
        try {
          return new Intl.DateTimeFormat(tenantLocale(), {
            day: "2-digit", month: "short", year: "numeric",
            timeZone: tenantTz(),
          }).format(new Date(iso));
        } catch { return "—"; }
      };
      const sections = slugOrder
        .filter((slug) => (bySlug.get(slug) || []).length > 0)
        .map((slug) => {
          // Feb 2026 — rows sorted alphabetically by rego so the printed
          // sheet reads in the same order dispatchers walk the yard;
          // makes tick-off much quicker than the raw storage-start order.
          const rowsForYard = [...(bySlug.get(slug) || [])].sort((a, b) =>
            (a.rego || "").localeCompare(b.rego || "", "en", { sensitivity: "base", numeric: true }),
          );
          const rowsHtml = rowsForYard.map((r, i) => `
            <tr>
              <td class="num">${i + 1}</td>
              <td class="mono">${esc(r.rego || "—")}</td>
              <td>${esc(r.vehicle || "—")}</td>
              <td>${esc(r.customer_name || "—")}</td>
              <td>${esc(fmtDropOff(r.storage_started_at))}</td>
              <td class="tick"></td>
            </tr>`).join("");
          return `
            <section class="yard">
              <h2>${esc(yardNameFor(slug))}
                <span class="count">${rowsForYard.length} vehicle${rowsForYard.length === 1 ? "" : "s"}</span>
              </h2>
              <table>
                <thead>
                  <tr>
                    <th class="num">#</th>
                    <th>REGO</th>
                    <th>Vehicle</th>
                    <th>Customer</th>
                    <th>Drop-off</th>
                    <th class="tick">✓</th>
                  </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
              </table>
            </section>`;
        }).join("");
      const html = `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8" />
<title>Yard Stock Take — ${esc(dateOnly)}</title>
<style>
  /* Print-margins live on @page only — layering body padding on top was
     pushing the footer onto a phantom last page with just a few tail rows. */
  @page { size: A4; margin: 10mm 10mm 8mm 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; }
  header { display: flex; align-items: baseline; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 4px; margin-bottom: 8px; }
  header h1 { margin: 0; font-size: 18px; letter-spacing: -0.01em; }
  header .stamp { font-size: 10px; color: #475569; }
  .summary { font-size: 10px; color: #475569; margin-bottom: 10px; }
  section.yard { margin-bottom: 10px; }
  section.yard h2 { margin: 0 0 3px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 2px; display: flex; align-items: baseline; gap: 10px; break-after: avoid; }
  section.yard h2 .count { font-size: 10px; color: #64748b; font-weight: 500; text-transform: none; letter-spacing: 0; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  thead th { background: #f1f5f9; text-align: left; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; font-size: 9px; color: #334155; padding: 4px 8px; border-bottom: 1px solid #cbd5e1; }
  tbody td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  td.num, th.num { width: 24px; color: #64748b; text-align: right; font-variant-numeric: tabular-nums; }
  td.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 700; letter-spacing: 0.01em; }
  td.tick, th.tick { width: 26px; text-align: center; }
  td.tick { border: 1px solid #94a3b8; border-left: none; border-right: 1px solid #e2e8f0; }
  td.tick::before { content: ""; display: inline-block; width: 12px; height: 12px; border: 1.5px solid #64748b; border-radius: 2px; vertical-align: middle; }
  footer { margin-top: 10px; padding-top: 4px; border-top: 1px solid #cbd5e1; font-size: 9.5px; color: #64748b; display: flex; justify-content: space-between; }
  .empty { text-align: center; padding: 40px 0; color: #94a3b8; font-style: italic; }
  @media print {
    /* Keep table rows intact but allow yard sections to span pages —
       forcing avoid on whole sections was overflowing the last page. */
    tr { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    footer { break-inside: avoid; page-break-inside: avoid; }
    .no-print { display: none !important; }
  }
  .no-print { position: fixed; top: 12px; right: 12px; }
  .no-print button { padding: 8px 16px; font-size: 13px; border: 1px solid #0f172a; background: #0f172a; color: white; border-radius: 4px; cursor: pointer; font-weight: 600; }
  .no-print button.secondary { background: white; color: #0f172a; margin-right: 6px; }
</style>
</head><body>
  <div class="no-print">
    <button class="secondary" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print</button>
  </div>
  <header>
    <h1>Yard Stock Take</h1>
    <div class="stamp">${esc(stampNz)}</div>
  </header>
  <div class="summary">Total vehicles across all yards: <strong>${data.length}</strong> · Manual check — tick each vehicle as you find it in the yard.</div>
  ${sections || `<div class="empty">No vehicles currently in any yard.</div>`}
  <footer>
    <span>Verified by: __________________________</span>
    <span>Signature / Date: __________________________</span>
  </footer>
  <script>setTimeout(() => window.print(), 300);</script>
</body></html>`;
      const w = window.open("", "_blank", "width=900,height=1100");
      if (!w) {
        toast.error("Pop-up blocked — please allow pop-ups for this site.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load stock take");
    } finally {
      setPrintingStockTake(false);
    }
  }, [yardsConfig]);

  return (
    <div className="max-w-6xl mx-auto py-6 px-4 sm:px-6" data-testid="yard-stock-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Warehouse className="h-6 w-6 text-slate-700 dark:text-slate-200" />
        <h1 className="font-display text-2xl sm:text-3xl font-black text-slate-900 dark:text-slate-100">
          Storage Yards
        </h1>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
        Every vehicle currently sitting in a company yard, with
        live storage accrual. When a vehicle leaves the yard, use &ldquo;On-tow out&rdquo;
        to create the outbound job — storage stops when a truck arrives
        on-site to load it. Abandoned / uninsured vehicles can be signed over
        as owned-in-lieu from here.
      </p>

      {/* Filters — yard tabs are built dynamically from the configured yards
          so slugs never drift between the config UI and this page. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setYardFilter("all")}
          className={
            "px-3 py-1.5 text-sm rounded-md border transition-colors " +
            (yardFilter === "all"
              ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100"
              : "border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800")
          }
          data-testid="yard-filter-all"
        >
          All Yards
        </button>
        {yardsConfig.map((y) => (
          <button
            key={y.slug}
            onClick={() => setYardFilter(y.slug)}
            className={
              "px-3 py-1.5 text-sm rounded-md border transition-colors " +
              (yardFilter === y.slug
                ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100"
                : "border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800")
            }
            data-testid={`yard-filter-${y.slug}`}
          >
            {y.name}
          </button>
        ))}

        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search rego, vehicle, colour, customer, phone, claim #, WO#, address…"
            className="pl-9 pr-9"
            data-testid="yard-search-input"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Clear search"
              data-testid="yard-search-clear"
            >
              <X className="h-3.5 w-3.5 text-slate-500" />
            </button>
          )}
        </div>

        <Button
          size="sm"
          variant="outline"
          onClick={onPrintStockTake}
          disabled={printingStockTake}
          className="font-semibold"
          title="Print a manual stock-take checklist across all yards"
          data-testid="yard-stock-take-btn"
        >
          {printingStockTake ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ClipboardList className="h-4 w-4" />
          )}
          Stock Take
        </Button>
      </div>

      {/* Summary strip */}
      <div
        className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-2.5 mb-4 flex items-center gap-6 text-xs font-mono text-slate-700 dark:text-slate-300"
        data-testid="yard-summary"
      >
        <span>Vehicles in yard: <strong>{summary.count}</strong></span>
        <span>Accrued storage: <strong className="text-emerald-700 dark:text-emerald-400">{fmtMoney(summary.totalStorage)}</strong></span>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Warehouse}
          title={q ? "No matches" : "No vehicles in this yard"}
          hint={q ? "Try a different search term, or clear the search to see everything." : "As soon as a tow drops off in this yard, storage tracking starts and the vehicle appears here."}
          testId="yard-empty"
        />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <YardRow
              key={r.id}
              row={r}
              rateCats={rateCats}
              loadingDetail={detailLoadingId === r.id}
              isAdmin={isAdmin}
              canSignOver={canSignOver}
              onOpenDepart={() => setDepartFor(r)}
              onOpenSignOver={() => setSignOverFor(r)}
              onOpenDva={() => setDvaConfirm({ row: r })}
              onOpenJob={() => openDetail(r)}
              onOpenBelongings={() => setBelongingsFor(r)}
              onRemoveStorage={() => setRemoveFor(r)}
              onRestore={() => setRestoreFor(r)}
              onRelink={async () => {
                if (relinkBusy) return;
                if (!window.confirm(`Search History for an already-completed on-tow-out job that matches ${r.rego || "this vehicle"} at ${r.yard_name || "this yard"}?\n\nIf a match is found the two jobs will be linked and this vehicle will drop off the yard list.`)) return;
                setRelinkBusy(r.id);
                try {
                  const res = await relinkYardOutbound(r.id);
                  if (res.linked) {
                    toast.success(`Linked to on-tow-out job ${res.outbound_rego || ""}. Vehicle removed from yard stock.`);
                  } else {
                    // Build a plain-english explanation from the
                    // diagnostic list — helps the dispatcher see
                    // WHY nothing matched (rego typo, wrong yard,
                    // time-window, or already linked elsewhere) so
                    // they don't have to blind-guess or (worse!) go
                    // and create a duplicate outbound.
                    const rej = Array.isArray(res.rejected) ? res.rejected : [];
                    if (rej.length > 0) {
                      const lines = rej.slice(0, 3).map((c) => {
                        const label = c.aa_job_id || c.work_order_number || c.rego || c.id?.slice(0, 8) || "?";
                        return `• ${label}: ${c.reason}`;
                      });
                      toast.error(
                        `No exact match. ${rej.length} near miss${rej.length === 1 ? "" : "es"}:\n${lines.join("\n")}`,
                        { duration: 12000 },
                      );
                    } else {
                      toast.info(res.detail || "No matching on-tow-out job found in History.");
                    }
                  }
                  refresh();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to search for a linked on-tow-out job");
                } finally {
                  setRelinkBusy(null);
                }
              }}
              relinkBusy={relinkBusy === r.id}
              onRateChange={async (category) => {
                try {
                  await setJobStorageRateCategory(r.id, category || null);
                  toast.success("Storage rate updated");
                  refresh();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to set storage rate");
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Depart-yard modal */}
      {departFor && (
        <DepartYardDialog
          parent={departFor}
          onClose={() => setDepartFor(null)}
          onDeparted={() => {
            setDepartFor(null);
            refresh();
          }}
        />
      )}
      {/* Sign-over-to-salvage modal */}
      {signOverFor && (
        <SignOverToNtsDialog
          parent={signOverFor}
          onClose={() => setSignOverFor(null)}
          onSignedOver={() => {
            setSignOverFor(null);
            refresh();
          }}
        />
      )}
      {/* Feb 2026 — Yes/No preflight for the DVA release. Auction houses
          typically cover storage until the vehicle is picked up by the
          purchaser; after that the PURCHASER wears the storage bill.
          If Yes, we open the DVA dialog in "restart-storage" mode
          (purchaser name is required, backend spawns a fresh storage
          job under the purchaser's name). If No, we open the normal
          DVA dialog. Skipped entirely on reprints of an already-released
          DVA — no state change to worry about. */}
      {dvaConfirm && !dvaConfirm.row.dva_released_at && (
        <Dialog open onOpenChange={(o) => { if (!o) setDvaConfirm(null); }}>
          <DialogContent data-testid="yard-dva-preflight">
            <DialogHeader>
              <DialogTitle>Restart storage for the purchaser?</DialogTitle>
              <DialogDescription>
                Auction houses (Turners, Manheim, DVA, etc.) usually cover
                storage up until the vehicle is collected. From today the
                purchaser will be responsible for storage fees.
                <br /><br />
                <strong>Yes</strong> starts a fresh storage job under the
                purchaser&apos;s name at the default light-vehicle rate
                (${(24.30).toFixed(2)}/day). The current storage job stops
                as normal.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setDvaFor(dvaConfirm.row); setDvaConfirm(null); }}
                data-testid="yard-dva-preflight-no"
              >
                No — storage stops
              </Button>
              <Button
                onClick={() => { setDvaFor({ ...dvaConfirm.row, __restart_storage: true }); setDvaConfirm(null); }}
                className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
                data-testid="yard-dva-preflight-yes"
              >
                Yes — restart under purchaser
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Skip preflight on reprint — go straight to the reprint DVA dialog. */}
      {dvaConfirm && dvaConfirm.row.dva_released_at && (() => {
        setDvaFor(dvaConfirm.row);
        setDvaConfirm(null);
        return null;
      })()}
      {/* Released DVA (auction) modal */}
      {dvaFor && (
        <ReleaseDvaDialog
          parent={dvaFor}
          restartStorage={!!dvaFor.__restart_storage}
          onClose={() => setDvaFor(null)}
          onReleased={() => {
            setDvaFor(null);
            refresh();
          }}
        />
      )}
      {/* Read-only job detail panel — opened by clicking a row or its
          clipboard icon. Same component as History uses so the two views
          stay consistent. */}
      <JobDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        truckById={truckById}
      />

      {/* Admin-only "delete storage row" confirm. Wipes storage tracking
          on the job but leaves the job itself intact — safe cleanup path
          for mistaken storage entries. */}
      {removeFor && (
        <ConfirmRemoveStorageDialog
          row={removeFor}
          onClose={() => setRemoveFor(null)}
          onRemoved={() => { setRemoveFor(null); refresh(); }}
        />
      )}
      {belongingsFor && (
        <BelongingsReleaseDialog
          row={belongingsFor}
          onClose={() => setBelongingsFor(null)}
          onLogged={() => { setBelongingsFor(null); refresh(); }}
        />
      )}
      {/* Restore-to-yard confirm — undoes an accidental DVA release. */}
      {restoreFor && (
        <Dialog open onOpenChange={(o) => { if (!o) setRestoreFor(null); }}>
          <DialogContent data-testid="yard-restore-dialog">
            <DialogHeader>
              <DialogTitle>Restore {restoreFor.rego || "vehicle"} to yard?</DialogTitle>
              <DialogDescription>
                This undoes the DVA (auction) release for{" "}
                <strong>{restoreFor.rego || restoreFor.vehicle || "this vehicle"}</strong>.
                The purchaser snapshot is cleared and the storage clock
                resumes from where it left off. An audit-trail entry is
                stamped on the job so the change is traceable.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRestoreFor(null)}
                disabled={restoreBusy}
                data-testid="yard-restore-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setRestoreBusy(true);
                  try {
                    await restoreYardJobToYard(restoreFor.id);
                    toast.success("Vehicle restored to yard.");
                    setRestoreFor(null);
                    refresh();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to restore vehicle to yard");
                  } finally {
                    setRestoreBusy(false);
                  }
                }}
                disabled={restoreBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="yard-restore-confirm"
              >
                {restoreBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Restore to yard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/**
 * BelongingsReleaseDialog — Feb 2026. Owner arrives at the counter to
 * collect personal items (child seat, phone, tools, laundry, etc.) from
 * a stored/wrecked vehicle before it's disposed of. This dialog:
 *   1. Captures collector name + items list (audit trail on the job).
 *   2. Opens the existing Vehicle Release Form PDF (`action=belongings_only`)
 *      in a new tab so the customer signs the physical release.
 * Vehicle stays in the yard — storage clock keeps ticking as normal.
 * Multiple pickups per job are supported (family makes several trips).
 */
function BelongingsReleaseDialog({ row, onClose, onLogged }) {
  const [collectorName, setCollectorName] = useState(row.customer_name || "");
  const [items, setItems] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const priorCount = (row.belongings_releases || []).length;

  const submit = async () => {
    const cn = collectorName.trim();
    const it = items.trim();
    if (cn.length < 2) { toast.error("Enter the name of the person collecting."); return; }
    if (it.length < 3) { toast.error("List at least one item collected."); return; }
    setBusy(true);
    try {
      await logBelongingsRelease(row.id, { collector_name: cn, items: it, notes: notes.trim() || null });
      toast.success("Belongings pickup logged — opening signed release form…");
      // Open the pre-ticked "Released Personal Items" release form. Uses
      // the axios-backed helper so the Bearer token is attached (a raw
      // window.open of the URL would 401 — the endpoint requires auth).
      // Non-blocking: failure to open the PDF still leaves the audit
      // entry in place.
      try {
        await openReleaseFormPrint(row.id, "belongings_only");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Pickup logged but the release form failed to open — try Reprint from the row.");
      }
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log pickup");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="belongings-release-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-slate-700 dark:text-slate-300" />
            Log Belongings Pickup
          </DialogTitle>
          <DialogDescription className="text-xs">
            {row.rego ? <span className="font-mono font-bold">{row.rego}</span> : "—"}
            {row.vehicle && <span className="text-slate-500 dark:text-slate-400"> · {row.vehicle}</span>}
            {priorCount > 0 && (
              <span className="ml-2 text-amber-700 dark:text-amber-400 font-semibold">
                (pickup #{priorCount + 1})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300" htmlFor="bel-collector">
              Collector name <span className="text-rose-500">*</span>
            </label>
            <Input
              id="bel-collector"
              value={collectorName}
              onChange={(e) => setCollectorName(e.target.value)}
              placeholder="Jane Smith"
              className="mt-1"
              disabled={busy}
              data-testid="belongings-collector"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300" htmlFor="bel-items">
              Items collected <span className="text-rose-500">*</span>
            </label>
            <Textarea
              id="bel-items"
              value={items}
              onChange={(e) => setItems(e.target.value)}
              placeholder="e.g. Child car seat, phone charger, sunglasses, gym bag"
              className="mt-1 h-24 text-sm"
              disabled={busy}
              data-testid="belongings-items"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300" htmlFor="bel-notes">
              Notes (optional)
            </label>
            <Textarea
              id="bel-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Owner returning next week for tools"
              className="mt-1 h-16 text-xs"
              disabled={busy}
              data-testid="belongings-notes"
            />
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 italic">
            The signed release PDF will open in a new tab after logging. The vehicle stays in the yard.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="belongings-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="belongings-confirm"
          >
            {busy ? <><Loader2 className="h-4 w-4.5 animate-spin" /> Logging…</> : <><Package className="h-4 w-4.5" /> Log &amp; Print Release</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmRemoveStorageDialog({ row, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${row.id}/storage/remove`);
      toast.success(`Storage cleared for ${row.rego || row.vehicle || "vehicle"} — job itself untouched.`);
      onRemoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear storage");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="remove-storage-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700 dark:text-rose-300">Delete storage row?</DialogTitle>
        </DialogHeader>
        <div className="text-sm space-y-2 py-2">
          <div>Clears storage tracking on <span className="font-semibold">{row.rego || row.vehicle}</span> — removes it from the yard list and the storage clock stops accruing.</div>
          <div className="text-xs text-slate-500">The job itself stays in History unchanged. This does NOT touch any impound record. Use this instead of Danger Zone purge for mistaken storage entries.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={busy} data-testid="remove-storage-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete storage row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function YardRow({ row, rateCats, loadingDetail, isAdmin, canSignOver, onOpenDepart, onOpenSignOver, onOpenDva, onOpenJob, onRateChange, onOpenBelongings, onRemoveStorage, onRestore, onRelink, relinkBusy }) {
  const yardBadgeColour = row.storage_yard === "parkers_road"
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
    : "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200";
  const isDvaReleased = !!row.dva_released_at;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenJob}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenJob(); } }}
      className={
        "rounded-md border p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer transition-colors " +
        (isDvaReleased
          ? "border-rose-200 bg-rose-50/40 hover:bg-rose-50 dark:border-rose-900/60 dark:bg-rose-950/20 dark:hover:bg-rose-950/40"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800/60")
      }
      data-testid={`yard-row-${row.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-black text-slate-900 dark:text-slate-100">
            {row.rego || "—"}
          </span>
          <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
            {row.vehicle || "—"}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${yardBadgeColour}`} data-testid={`yard-badge-${row.id}`}>
            {row.yard_name}
          </span>
          {isDvaReleased && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200 font-semibold"
              data-testid={`yard-dva-badge-${row.id}`}
              title={`Released to auction purchaser on ${fmtDate(row.dva_released_at)}`}
            >
              Released – auctioned
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex flex-wrap items-center gap-1.5">
          <span>{row.customer_name || "—"}</span>
          {(row.photos_count || 0) > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 py-0.5 rounded"
              title={`${row.photos_count} damage photo${row.photos_count === 1 ? "" : "s"}`}
              data-testid={`yard-photos-badge-${row.id}`}
            >
              <Camera className="h-2.5 w-2.5" /> {row.photos_count}
            </span>
          )}
          {row.program && <span>· {row.program}</span>}
          {row.work_order_number && <span>· <code className="font-mono">{row.work_order_number}</code></span>}
          {row.aa_job_id && <span>· <code className="font-mono">{row.aa_job_id}</code></span>}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Dropped off {fmtDate(row.completed_at)}
          {isDvaReleased && (
            <> · <span className="text-rose-700 dark:text-rose-300">Released {fmtDate(row.dva_released_at)} by {row.dva_released_by || "—"}</span></>
          )}
        </div>
      </div>

      <div className="text-right shrink-0">
        <div className="text-2xl font-black text-emerald-700 dark:text-emerald-400 tabular-nums" data-testid={`yard-storage-total-${row.id}`}>
          {fmtMoney(row.storage_total)}
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
          {row.days_in_storage} day{row.days_in_storage === 1 ? "" : "s"} @ {fmtMoney(row.daily_rate)} ex-GST
        </div>
        {/* Party rate category — auto-detected, always overridable. Amber
            "confirm" flag when it fell through to the impound default. */}
        <div className="mt-1 flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          {row.rate_source === "default" && (
            <span
              className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
              title="No party matched — using the impound default rate. Pick the right category if this is AA/FR, Police, Insurance or Turners."
              data-testid={`yard-rate-confirm-${row.id}`}
            >
              Confirm rate
            </span>
          )}
          <select
            value={row.rate_override || ""}
            onChange={(e) => onRateChange(e.target.value)}
            className="text-[10px] rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-1 py-0.5 max-w-[170px]"
            title={`Storage rate: ${row.rate_category_label || "Default"} (${row.rate_source === "manual" ? "set manually" : row.rate_source === "auto" ? "auto-detected" : "default"})`}
            data-testid={`yard-rate-select-${row.id}`}
          >
            <option value="">
              {row.rate_source === "auto" && row.rate_category_label
                ? `Auto · ${row.rate_category_label}`
                : "Auto-detect"}
            </option>
            {rateCats.map((c) => (
              <option key={c.key} value={c.key}>
                {`${c.label} · $${Number(c.rate_incl_gst).toFixed(2)} incl`}
              </option>
            ))}
            <option value="default">Default (impound rate)</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenJob}
          disabled={loadingDetail}
          data-testid={`yard-open-job-${row.id}`}
          title="View original job"
        >
          {loadingDetail ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
        </Button>
        {isDvaReleased ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onRestore?.(); }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
              data-testid={`yard-restore-btn-${row.id}`}
              title="Undo DVA release — put this vehicle back on the yard stock list"
            >
              <RotateCcw className="h-4 w-4.5" /> Restore to yard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenDva}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
              data-testid={`yard-dva-reprint-btn-${row.id}`}
              title="Reprint DVA release form"
            >
              <Printer className="h-4 w-4.5" /> Reprint DVA
            </Button>
          </>
        ) : (
          <>
            {canSignOver && (
              <Button
                size="sm"
                variant="outline"
                onClick={onOpenSignOver}
                className="font-semibold"
                data-testid={`yard-signover-btn-${row.id}`}
                title={`Owner abandoned / uninsured — sign over to ${salvageShort()}`}
              >
                <FileWarning className="h-4 w-4.5" /> Sign Over
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenDva}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
              data-testid={`yard-dva-btn-${row.id}`}
              title="Vehicle sold at 3rd-party auction (DVA)"
            >
              <Gavel className="h-4 w-4.5" /> Released
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onOpenBelongings?.(); }}
              className="font-semibold"
              data-testid={`yard-belongings-btn-${row.id}`}
              title="Log personal items collected by owner + print signed release"
            >
              <Package className="h-4 w-4.5" /> Belongings
              {Array.isArray(row.belongings_releases) && row.belongings_releases.length > 0 && (
                <span className="ml-1.5 text-[10px] font-bold text-slate-500 dark:text-slate-400">
                  ·{row.belongings_releases.length > 1 ? ` ×${row.belongings_releases.length}` : " ✓"}
                </span>
              )}
            </Button>
            <Button
              onClick={onOpenDepart}
              size="sm"
              variant="outline"
              className="font-semibold"
              data-testid={`yard-depart-btn-${row.id}`}
            >
              <TruckIcon className="h-4 w-4.5" /> On-tow out
            </Button>
            {/* Retro reverse-linker — for the case where the on-tow-out
                job already exists in History (partner poller closed it)
                but was never glued to this yarded parent. Server
                searches for a match and stops the clock if found. */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onRelink?.(); }}
              disabled={!!relinkBusy}
              className="border-sky-300 text-sky-700 hover:bg-sky-50 dark:border-sky-800 dark:text-sky-300 dark:hover:bg-sky-950/30"
              data-testid={`yard-relink-btn-${row.id}`}
              title="Link this vehicle to an on-tow-out job that's already been completed (fixes the A63707545 stuck-in-yard case)"
            >
              {relinkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4.5" />}
            </Button>
            {isAdmin && (
              // Admin-only escape hatch for mistaken storage entries. Wipes
              // storage tracking on the job but leaves the job intact.
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onRemoveStorage?.(); }}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
                data-testid={`yard-remove-storage-btn-${row.id}`}
                title="Admin: delete this storage row (leaves the job untouched)"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * SignOverToNtsDialog — two-step confirmation for signing a yarded vehicle
 * over to the tenant's own salvage entity (typical scenario: owner walked
 * away, vehicle is uninsured / bad debt written off). Requires the write-off
 * amount so the Owned in Lieu reconciliation is accurate. Second confirmation
 * click required to actually fire. Reversible for 48h from Owned in Lieu.
 *
 * Clicking Sign Over prints BOTH documents without further clicks: the
 * signable sign-over form (Customer Copy + Office Copy, server-rendered) and
 * the write-off record. The two are deliberately sequenced — see `submit`.
 */
function SignOverToNtsDialog({ parent, onClose, onSignedOver }) {
  // Oct 2026 — this screen used to save silently: no signable sign-over form
  // for the owner, no write-off record, while the same action started from
  // History printed both. Same endpoint, different paper trail.
  const [receipt, setReceipt] = useState(null);
  const [amountOwed, setAmountOwed] = useState(
    parent.storage_total != null ? String(parent.storage_total.toFixed(2)) : ""
  );
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formPrinted, setFormPrinted] = useState(false);
  const focusPrintRef = useRef(null);

  // The sign-over form prints in its OWN tab, so firing window.print() for the
  // write-off record at the same moment stacks two print dialogs across two
  // tabs. Instead the record waits until the dispatcher comes back to
  // Tow-Hook, which also matches the order they handle the paper in.
  const printRecordOnReturn = () => {
    if (document.hasFocus()) {
      setTimeout(() => window.print(), 250);
      return;
    }
    const once = () => {
      window.removeEventListener("focus", once);
      focusPrintRef.current = null;
      setTimeout(() => window.print(), 300);
    };
    focusPrintRef.current = once;
    window.addEventListener("focus", once);
  };

  // A queued print must not fire after the dialog is gone — the print-only
  // record unmounts with it and the browser would print the dispatcher board.
  useEffect(() => () => {
    if (focusPrintRef.current) {
      window.removeEventListener("focus", focusPrintRef.current);
    }
  }, []);

  const submit = async () => {
    const amt = Number(amountOwed);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Enter a valid write-off amount (0 or more)");
      return;
    }
    if (!confirmed) {
      toast.error("Please tick the confirmation box");
      return;
    }
    // Opened synchronously, INSIDE the click, because popup blockers reject
    // window.open once an await has broken the user gesture — which would
    // silently swallow the form the owner is standing there to sign.
    const formTab = window.open("", "_blank");
    setSaving(true);
    try {
      const updated = await releaseJobToNts(parent.id, {
        amount_owed: amt,
        release_notes: notes.trim(),
      });
      setReceipt({
        ...parent,
        ...(updated || {}),
        release_notes: notes.trim(),
        // Yard jobs carry no pricing engine, so the write-off record has
        // nothing to total up — feed it the amount that was typed.
        pricing_breakdown: updated?.pricing_breakdown || { total: amt },
      });

      let printedForm = false;
      try {
        const html = await fetchReleaseFormHtml(parent.id, "signover_to_nts");
        renderReleaseFormInTab(html, formTab);
        printedForm = true;
      } catch (formErr) {
        // The sign-over itself already succeeded and must not be rolled back
        // for a paperwork failure — tell them plainly and leave the manual
        // button as the fallback.
        try { formTab?.close(); } catch { /* already gone */ }
        toast.warning(
          `Signed over, but the sign-over form didn't print: ${releaseFormError(formErr, "could not build the form")}. Use Print Sign-Over Form below.`
        );
      }
      setFormPrinted(printedForm);

      toast.success(
        `${parent.rego || parent.vehicle} signed over to ${salvageShort()}. $${amt.toFixed(2)} recorded as bad debt.`
      );
      printRecordOnReturn();
      // `onSignedOver` refreshes the list and unmounts this dialog, which
      // would rip the print-only record out of the page mid-print — it runs
      // when the dispatcher closes the record below.
    } catch (e) {
      try { formTab?.close(); } catch { /* already gone */ }
      toast.error(e.response?.data?.detail || `Failed to sign over to ${salvageShort()}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={!receipt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="yard-signover-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-800 dark:text-amber-300">
            Sign {parent.rego || parent.vehicle} over to {salvageShort()}
          </DialogTitle>
          <DialogDescription>
            Use this when the owner has abandoned the vehicle or is uninsured
            and unable to pay. Storage stops immediately, the vehicle exits
            this yard list, and it appears on the <strong>{salvageShort()} Owned in Lieu</strong>{" "}
            page for the billing user to reconcile against scrap / auction income.
            This action cannot be undone from the yard page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-2.5 text-xs text-slate-700 dark:text-slate-300 font-mono">
            <div>Rego: <strong>{parent.rego || "—"}</strong></div>
            <div>Vehicle: {parent.vehicle || "—"}</div>
            <div>Customer: {parent.customer_name || "—"}</div>
            <div>In storage: {parent.days_in_storage} day{parent.days_in_storage === 1 ? "" : "s"} · Accrued: <strong className="text-emerald-700 dark:text-emerald-400">{fmtMoney(parent.storage_total)}</strong></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Amount owed (write-off) <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amountOwed}
                onChange={(e) => setAmountOwed(e.target.value)}
                placeholder="Tow + storage + admin fees"
                className="pl-6"
                data-testid="yard-signover-amount-input"
              />
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-500 mt-1">
              Pre-filled with current accrued storage. Add tow / admin fees if applicable.
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Notes (optional)
            </label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Owner uncontactable since 12 Jan, vehicle uninsured"
              data-testid="yard-signover-notes-input"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
              data-testid="yard-signover-confirm-checkbox"
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              I confirm the owner has abandoned or is unable to pay for this vehicle
              and it should be signed over to {salvageFull()} as {salvageLabel()}-owned
              inventory.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="yard-signover-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !confirmed || !amountOwed}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="yard-signover-submit"
          >
            {saving ? (
              <Loader2 className="h-4 w-4.5 animate-spin" />
            ) : (
              <FileWarning className="h-4 w-4.5" />
            )}
            Sign Over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {receipt && (
      <Dialog open onOpenChange={(o) => { if (!o) { setReceipt(null); onSignedOver(); } }}>
        <DialogContent data-testid="yard-signover-done-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-800 dark:text-amber-300">
              Signed over to {salvageShort()}
            </DialogTitle>
            <DialogDescription>
              {formPrinted
                ? "The sign-over form has opened in a new tab — Customer Copy and Office Copy. The write-off record prints when you come back to this tab."
                : "The write-off record is printing. Print the sign-over form if the owner is here to sign it."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <PrintReleaseFormButton
              jobId={receipt.id}
              action="signover_to_nts"
              label={formPrinted ? "Re-print Sign-Over Form" : "Print Sign-Over Form"}
              testId="yard-signover-print-form"
            />
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="yard-signover-reprint"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Re-print Record
            </Button>
            <Button
              onClick={() => { setReceipt(null); onSignedOver(); }}
              className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
              data-testid="yard-signover-done"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {receipt && <PrintReceipt job={receipt} mode="nts" />}
    </>
  );
}

function DepartYardDialog({ parent, onClose, onDeparted }) {
  const [dropAddress, setDropAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!dropAddress.trim()) {
      toast.error("Drop-off address is required");
      return;
    }
    setSaving(true);
    try {
      await departYard(parent.id, {
        drop_off_address: dropAddress.trim(),
        notes: notes.trim(),
        service_type: "tow",
      });
      toast.success(`Outbound tow created for ${parent.rego || parent.vehicle}. Storage stops when the truck arrives on-site.`);
      // Broadcast so the Dashboard / Jobs Queue picks up the new pending
      // outbound immediately — otherwise the dispatcher navigates there
      // and doesn't see it until the 15s auto-refresh, which is what led
      // to the Feb 2026 "I assigned it and it disappeared" report (they
      // were seeing a stale jobs array when they clicked Assign).
      window.dispatchEvent(new Event("jobs:refresh"));
      onDeparted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create outbound job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="yard-depart-dialog">
        <DialogHeader>
          <DialogTitle>On-tow {parent.rego || parent.vehicle} out of the yard</DialogTitle>
          <DialogDescription>
            Creates a fresh outbound tow job. Pickup is <strong>{parent.yard_name}</strong>{" "}
            (auto-filled). Storage will stop accruing on the original job the
            moment your truck reaches <strong>on-site</strong> at the yard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Drop-off address <span className="text-rose-500">*</span>
            </label>
            <Input
              value={dropAddress}
              onChange={(e) => setDropAddress(e.target.value)}
              placeholder="e.g. Panelbeater or repair shop, with address"
              data-testid="yard-depart-address-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Notes for the driver (optional)
            </label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions"
              data-testid="yard-depart-notes-input"
            />
          </div>
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-2.5 text-xs text-slate-700 dark:text-slate-300 font-mono">
            <div>Rego: <strong>{parent.rego || "—"}</strong></div>
            <div>Vehicle: {parent.vehicle || "—"}</div>
            <div>Currently in storage {parent.days_in_storage} day{parent.days_in_storage === 1 ? "" : "s"} · Accrued: <strong className="text-emerald-700 dark:text-emerald-400">{fmtMoney(parent.storage_total)}</strong></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="yard-depart-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !dropAddress.trim()}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="yard-depart-submit"
          >
            {saving ? (
              <Loader2 className="h-4 w-4.5 animate-spin" />
            ) : (
              <ArrowUpRight className="h-4 w-4.5" />
            )}
            Create Outbound Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ReleaseDvaDialog — vehicle was auctioned off by a 3rd-party auction house
 * (DVA). Stops the storage clock, captures purchaser details, and opens the
 * release-form PDF in a new tab for printing. NTS is not involved in the
 * sale price, so nothing monetary appears on the paperwork.
 *
 * Idempotent: if the vehicle has already been DVA-released, this becomes
 * a "Reprint" dialog and preserves the original release timestamp.
 */
function ReleaseDvaDialog({ parent, restartStorage = false, onClose, onReleased }) {
  const alreadyReleased = !!parent.dva_released_at;
  const [purchaserName, setPurchaserName] = useState(parent.dva_purchaser_name || "");
  const [purchaserDetails, setPurchaserDetails] = useState(parent.dva_purchaser_details || "");
  const [saving, setSaving] = useState(false);
  // Feb 2026 — when the dispatcher chose "Yes — restart under purchaser"
  // in the preflight, we require a non-empty purchaser name so the new
  // storage job has an owner to bill.
  const purchaserRequired = restartStorage && !alreadyReleased;

  const submit = async () => {
    if (purchaserRequired && !purchaserName.trim()) {
      toast.error("Purchaser name is required when restarting storage.");
      return;
    }
    setSaving(true);
    try {
      const blob = await releaseYardJobDva(parent.id, {
        purchaser_name: purchaserName.trim(),
        purchaser_details: purchaserDetails.trim(),
        restart_storage_for_purchaser: purchaserRequired,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(
        alreadyReleased
          ? "DVA release form reprinted."
          : purchaserRequired
            ? `${parent.rego || parent.vehicle} released. Storage restarted under ${purchaserName.trim()}.`
            : `${parent.rego || parent.vehicle} released to auction purchaser. Storage stopped.`
      );
      onReleased();
    } catch (e) {
      // Blob-typed error responses have JSON hidden inside — surface it.
      let detail = "Failed to generate DVA release form";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* ignore */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const reprint = async () => {
    setSaving(true);
    try {
      const blob = await fetchDvaReleasePdf(parent.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("DVA release form reprinted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reprint DVA release form");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="yard-dva-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-800 dark:text-rose-300">
            {alreadyReleased ? "Reprint DVA release" : "Released DVA"} — {parent.rego || parent.vehicle}
          </DialogTitle>
          <DialogDescription>
            {alreadyReleased ? (
              <>
                This vehicle was released to the auction purchaser on{" "}
                <strong>{fmtDate(parent.dva_released_at)}</strong>. Update the
                purchaser details below if you need to correct them, then
                reprint the release form.
              </>
            ) : (
              <>
                Use this when the vehicle has been sold at a 3rd-party auction
                (DVA) and is being handed to the purchaser. Storage stops
                immediately. The vehicle stays visible here for 14 days for
                reprints, then auto-archives.
                <br />
                <span className="text-[11px] italic">
                  Sale price is not recorded — {salvageLabel()} bills tow &amp; storage only.
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-2.5 text-xs text-slate-700 dark:text-slate-300 font-mono">
            <div>Rego: <strong>{parent.rego || "—"}</strong></div>
            <div>Vehicle: {parent.vehicle || "—"}</div>
            <div>Yard: {parent.yard_name || "—"}</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Purchaser name {purchaserRequired ? <span className="text-rose-500">*</span> : "(optional)"}
            </label>
            <Input
              value={purchaserName}
              onChange={(e) => setPurchaserName(e.target.value)}
              placeholder={purchaserRequired ? "Required — storage will restart under this name" : "Leave blank to hand-write on the form"}
              className={purchaserRequired && !purchaserName.trim() ? "border-rose-400" : ""}
              data-testid="yard-dva-purchaser-name-input"
            />
            {purchaserRequired && (
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1 italic">
                A new storage job will start under this name at $24.30/day.
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Purchaser details (optional)
            </label>
            <Textarea
              rows={4}
              value={purchaserDetails}
              onChange={(e) => setPurchaserDetails(e.target.value)}
              placeholder="Address, phone, drivers licence, anything the auction house gave you. Leave blank for a hand-fill form."
              data-testid="yard-dva-purchaser-details-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="yard-dva-cancel">
            Cancel
          </Button>
          {alreadyReleased && (
            <Button
              variant="outline"
              onClick={reprint}
              disabled={saving}
              data-testid="yard-dva-reprint-only"
            >
              {saving ? <Loader2 className="h-4 w-4.5 animate-spin" /> : <Printer className="h-4 w-4.5" />}
              Reprint (no changes)
            </Button>
          )}
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-rose-700 hover:bg-rose-800 text-white"
            data-testid="yard-dva-submit"
          >
            {saving ? (
              <Loader2 className="h-4 w-4.5 animate-spin" />
            ) : (
              <Gavel className="h-4 w-4.5" />
            )}
            {alreadyReleased ? "Save & Reprint" : "Release & Print Form"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
